/**
 * Los 12 pasos del registro del Guru, en orden, con el sitio donde se llena
 * cada uno.
 *
 * Vive acá y no dentro de una pantalla porque lo consumen tres cosas que antes
 * mantenían su propia copia: la barra de progreso que acompaña al guru por toda
 * la app, las tarjetas del hub y el panel del admin. Cuando cada una tenía su
 * lista, bastaba con agregar un campo (pasó con los subtemas y las industrias)
 * para que empezaran a decir cosas distintas sobre el mismo registro.
 *
 * El estado sale íntegro de `guru_setup_status`: acá NO se calcula nada, solo se
 * ordena y se le pone dirección. La cuenta —12 ítems— tiene que coincidir con
 * `total_count` de esa RPC, y por eso el orden manda: es el camino que le
 * proponemos al guru, de lo que se ve primero a lo que desbloquea el cobro.
 */
import type { GuruSetupStatus } from '@/lib/api/account';

export interface GuruSetupItem {
  /**
   * Identificador estable. El código discrimina por acá, NUNCA por `label`:
   * el label es copy y está para cambiarse, y un `if (label === '…')` se
   * apagaría en silencio la primera vez que alguien mejore la redacción.
   */
  key:
    | 'avatar' | 'headline' | 'bio' | 'languages' | 'categories'
    | 'core_topics' | 'industries' | 'availability' | 'calendar'
    | 'payout' | 'legals' | 'tax';
  /** Cómo lo nombramos en TODAS partes: hub, barra, admin y correos. */
  label: string;
  done: boolean;
  /** Dónde se llena. Un solo clic, sin buscar la pestaña. */
  to: string;
  /** Para agrupar en las tarjetas del hub. */
  grupo: 'foto' | 'perfil' | 'agenda' | 'legal';
}

export function guruSetupItems(s: GuruSetupStatus): GuruSetupItem[] {
  const cuenta = '/account';
  return [
    // Primero lo que se ve: sin foto ni titular no hay ficha que mostrar.
    { key: 'avatar',      label: 'Foto de perfil',    done: s.profile.avatar,      to: `${cuenta}?tab=foto`,        grupo: 'foto' },
    { key: 'headline',    label: 'Titular',           done: s.profile.headline,    to: `${cuenta}?tab=personal`,    grupo: 'perfil' },
    { key: 'bio',         label: 'Bio extendida',     done: s.profile.bio,         to: `${cuenta}?tab=profesional`, grupo: 'perfil' },
    { key: 'languages',   label: 'Idiomas',           done: s.profile.languages,   to: `${cuenta}?tab=profesional`, grupo: 'perfil' },
    // Después lo que lo hace encontrable.
    { key: 'categories',  label: 'Categorías',        done: s.profile.specialties, to: `${cuenta}?tab=profesional`, grupo: 'perfil' },
    { key: 'core_topics', label: 'Subtemas core',     done: s.profile.core_topics, to: `${cuenta}?tab=profesional`, grupo: 'perfil' },
    { key: 'industries',  label: 'Industrias',        done: s.profile.industries,  to: `${cuenta}?tab=profesional`, grupo: 'perfil' },
    // Después lo que permite que le reserven.
    { key: 'availability', label: 'Disponibilidad',   done: s.operations.availability_set,   to: '/guru/availability', grupo: 'agenda' },
    { key: 'calendar',    label: 'Google Calendar',   done: s.operations.calendar_connected, to: '/guru/availability', grupo: 'agenda' },
    // Y al final lo que desbloquea el cobro.
    { key: 'payout',      label: 'Método de pago',    done: s.payments_legal.payout_method_set, to: `${cuenta}?tab=pagos`, grupo: 'legal' },
    { key: 'legals',      label: 'Acuerdos legales',  done: s.payments_legal.all_legals_signed, to: '/guru/onboarding',    grupo: 'legal' },
    { key: 'tax',         label: 'Formulario fiscal', done: s.payments_legal.tax_doc_approved,  to: '/guru/onboarding',    grupo: 'legal' },
  ];
}

/** El primero que falta, en el orden propuesto. `null` si no falta nada. */
export function siguientePendiente(s: GuruSetupStatus): GuruSetupItem | null {
  return guruSetupItems(s).find((i) => !i.done) ?? null;
}
